import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { PaginationState, SortingState } from "@tanstack/react-table";
import { DataTable } from "@/components/Base/Datatable";
import { Input } from "@/components/Base/Input";
import { Skeleton } from "@/components/Base/Skeleton";
import { useAllShops } from "@/hooks/queries/useAllShops";
import {
  useShopConfigAuditRows,
  ShopAuditRow,
} from "@/hooks/queries/useShopConfigAuditRows";
import {
  ShopSortKey,
  ShopSortOrder,
  filterAndSortShops,
} from "@/lib/shopAuditList";
import { shopConfigAuditColumns } from "./ShopConfigAudit.columns";

interface ShopConfigAuditTableProps {
  selectedShopId: string | null;
  onSelectRow: (row: ShopAuditRow) => void;
  onRowsChange: (rows: ShopAuditRow[]) => void;
}

const DEFAULT_PAGE_SIZE = 10;
// The largest option in DataTable's page-size menu. Each visible shop costs
// two extra reads (tags + automations), so a crafted ?pageSize= must not be
// able to fan out beyond what the UI itself offers.
const MAX_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;
const SORT_KEYS: ShopSortKey[] = ["name", "createdAt"];

const positiveInt = (value: string | null, fallback: number) => {
  const parsed = parseInt(value ?? "", 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

// Everything the table shows lives in the URL (page, pageSize, search,
// sortBy, sortOrder). The shop list is read once (useAllShops) and searched,
// sorted and paged in memory; only the visible page's tags/automations are
// fetched.
export const ShopConfigAuditTable = ({
  selectedShopId,
  onSelectRow,
  onRowsChange,
}: ShopConfigAuditTableProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const pageSize = Math.min(
    positiveInt(searchParams.get("pageSize"), DEFAULT_PAGE_SIZE),
    MAX_PAGE_SIZE
  );
  const requestedPage = positiveInt(searchParams.get("page"), 1);
  const search = searchParams.get("search") ?? "";
  const sortByParam = searchParams.get("sortBy");
  const sortOrderParam = searchParams.get("sortOrder");
  // An unknown/absent sort falls back to newest-created first, which is not
  // reported to the headers as an active sort (see `sorting` below).
  const explicitSort =
    SORT_KEYS.includes(sortByParam as ShopSortKey) &&
    (sortOrderParam === "asc" || sortOrderParam === "desc");
  const sortBy: ShopSortKey = explicitSort
    ? (sortByParam as ShopSortKey)
    : "createdAt";
  const sortOrder: ShopSortOrder = explicitSort
    ? (sortOrderParam as ShopSortOrder)
    : "desc";

  const {
    data: allShops,
    isLoading: shopsLoading,
    isError: shopsError,
  } = useAllShops();

  const filteredShops = useMemo(
    () => filterAndSortShops(allShops ?? [], { search, sortBy, sortOrder }),
    [allShops, search, sortBy, sortOrder]
  );
  const totalPages = Math.max(1, Math.ceil(filteredShops.length / pageSize));
  const pageIndex = Math.min(requestedPage, totalPages) - 1;
  const pageShops = useMemo(
    () => filteredShops.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize),
    [filteredShops, pageIndex, pageSize]
  );

  const {
    data: auditRows,
    isLoading: auditLoading,
    isError: auditError,
  } = useShopConfigAuditRows(pageShops);

  // react-query and useMemo keep `auditRows` referentially stable between
  // renders, so this fires only when the visible rows actually change.
  useEffect(() => {
    onRowsChange(auditRows ?? []);
  }, [auditRows, onRowsChange]);

  // The debounced search write fires from a timer created in an earlier
  // render. Building its URL from that render's `searchParams` would revert
  // a sort or page change made in the meantime, so every write reads the
  // params as they are now.
  const latestParams = useRef(searchParams);
  latestParams.current = searchParams;

  const updateParams = (change: (params: URLSearchParams) => void) => {
    const next = new URLSearchParams(latestParams.current);
    change(next);
    setSearchParams(next, { replace: true });
  };

  // Typing updates local state at once; the URL (and so the filter, and the
  // per-page tags/automations reads) follow after a pause.
  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => {
    if (searchInput.trim() === search.trim()) return;
    const timer = setTimeout(() => {
      updateParams((params) => {
        if (searchInput.trim()) params.set("search", searchInput.trim());
        else params.delete("search");
        params.set("page", "1");
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const pagination: PaginationState = { pageIndex, pageSize };
  const setPagination: Dispatch<SetStateAction<PaginationState>> = (
    updater
  ) => {
    const next = typeof updater === "function" ? updater(pagination) : updater;
    if (next.pageIndex === pageIndex && next.pageSize === pageSize) return;
    updateParams((params) => {
      params.set("page", String(next.pageIndex + 1));
      params.set("pageSize", String(next.pageSize));
    });
  };

  const sorting: SortingState = explicitSort
    ? [{ id: sortBy, desc: sortOrder === "desc" }]
    : [];
  const setSorting: Dispatch<SetStateAction<SortingState>> = (updater) => {
    const next = typeof updater === "function" ? updater(sorting) : updater;
    updateParams((params) => {
      const [sort] = next;
      if (sort && SORT_KEYS.includes(sort.id as ShopSortKey)) {
        params.set("sortBy", sort.id);
        params.set("sortOrder", sort.desc ? "desc" : "asc");
      } else {
        params.delete("sortBy");
        params.delete("sortOrder");
      }
      params.set("page", "1");
    });
  };

  const columns = useMemo(
    () => shopConfigAuditColumns({ onSelectRow }),
    [onSelectRow]
  );

  // The search box stays mounted in every state below: a new term changes the
  // visible shops, which reloads their tags/automations and swaps in the
  // skeleton, and unmounting the input then would drop focus mid-typing.
  let content;
  if (shopsError || auditError) {
    content = (
      <div role="alert" className="text-sm text-red-300">
        Couldn&apos;t load shops. Reload the page to try again.
      </div>
    );
  } else if (shopsLoading || auditLoading || !auditRows) {
    content = (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-12 w-24 self-end" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  } else {
    content = (
      <DataTable
        data={auditRows}
        columns={columns}
        pagination={pagination}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
        rowCount={filteredShops.length}
        onRowClick={(row) => onSelectRow(row)}
        // Highlight only: controlled selection state + row id, deliberately
        // WITHOUT enableRowSelection (which would inject a checkbox column).
        getRowId={(row) => row.shop.id}
        rowSelection={selectedShopId ? { [selectedShopId]: true } : {}}
        rowClassName="cursor-pointer data-[state=selected]:bg-white/10"
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div>
        <Input
          type="search"
          aria-label="Search shops by name"
          className="w-64 text-primary"
          placeholder="Search shops by name"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
      </div>
      {content}
    </div>
  );
};
